import { DEFAULT_RANGE, emptyWeek, nextOpenDate } from "@/helpers/availability";
import { AvailabilityException, WeeklyDay } from "@/interfaces/Availability";
import { faChevronDown, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { FC } from "react";
import { Form } from "react-bootstrap";
import RangeListEditor from "./RangeListEditor";

interface IProps {
  value: AvailabilityException[];
  onChange: (value: AvailabilityException[]) => void;
  errors: Record<string, string>;
  /** Ligne ouverte, pilotée par la modale (un clic dans l'aperçu du mois l'ouvre aussi). */
  openIndex: number | null;
  onOpenChange: (index: number | null) => void;
  /** yyyy-MM-dd, heure de Paris. */
  today: string;
  /** Semaine type en cours : un nouveau congé tombe sur le prochain jour ouvert. */
  weekly?: WeeklyDay[];
  readOnly?: boolean;
}

const shortDate = (date: string) =>
  DateTime.fromISO(date).setLocale("fr").toFormat("ccc d LLL");

export const exceptionLabel = (exception: AvailabilityException) =>
  exception.startDate === exception.endDate
    ? shortDate(exception.startDate)
    : `${shortDate(exception.startDate)} → ${shortDate(exception.endDate)}`;

/** « 3 jours » pour une période, rien pour un seul jour ou des dates invalides. */
const periodLength = (exception: AvailabilityException) => {
  const start = DateTime.fromISO(exception.startDate, { zone: "utc" });
  const end = DateTime.fromISO(exception.endDate, { zone: "utc" });
  if (!start.isValid || !end.isValid || end <= start) return null;
  return `${end.diff(start, "days").days + 1} jours`;
};

/**
 * Congés et horaires particuliers : des dates qui remplacent la semaine type.
 * (Le modèle les appelle « exceptions » ; l'écran parle le langage de l'équipe.)
 */
const AvailabilityExceptionsEditor: FC<IProps> = ({
  value,
  onChange,
  errors,
  openIndex,
  onOpenChange,
  today,
  weekly = emptyWeek(),
  readOnly = false,
}) => {
  const update = (index: number, patch: Partial<AvailabilityException>) =>
    onChange(value.map((e, i) => (i === index ? { ...e, ...patch } : e)));

  const add = () => {
    const date = nextOpenDate(weekly, today);
    onChange([...value, { startDate: date, endDate: date, isOff: true, ranges: [] }]);
    onOpenChange(value.length);
  };

  const addButton = (
    <button type="button" className="fx-availability__link" onClick={add}>
      <FontAwesomeIcon icon={faPlus} className="me-1" />
      Ajouter un congé
    </button>
  );

  return (
    <section aria-labelledby="availability-exceptions-title">
      <div className="fx-availability__head">
        <h3 id="availability-exceptions-title" className="fx-availability__title">
          Congés et horaires particuliers
        </h3>
      </div>
      <p className="fx-availability__hint">
        Ces dates remplacent la semaine type : absence complète, ou horaires différents
        (une demi-journée). Les dates passées disparaissent à l'enregistrement.
      </p>

      {value.length === 0 ? (
        <div className="fx-empty">
          <span>Aucun congé ni horaire particulier à venir.</span>
          {!readOnly && addButton}
        </div>
      ) : (
        <>
          <ul className="fx-exceptions">
            {value.map((exception, index) => {
              const isOpen = openIndex === index;
              const label = exceptionLabel(exception);
              const length = periodLength(exception);
              const panelId = `availability-exception-${index}`;
              const dateError = errors[`exceptions.${index}`];
              return (
                <li key={index} className={`fx-exception ${isOpen ? "fx-exception--open" : ""}`}>
                  <button
                    type="button"
                    className="fx-exception__summary"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => onOpenChange(isOpen ? null : index)}
                  >
                    <span className="fx-exception__dates">
                      {label}
                      {length && <span className="fx-exception__length"> · {length}</span>}
                    </span>
                    <span className={`fx-tag ${exception.isOff ? "fx-tag--off" : "fx-tag--hours"}`}>
                      {exception.isOff ? "Congé" : "Horaires particuliers"}
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} className="fx-exception__chevron" />
                  </button>

                  {isOpen && (
                    <div id={panelId} className="fx-exception__body">
                      {readOnly ? (
                        <RangeListEditor
                          ranges={exception.ranges}
                          onChange={() => undefined}
                          errorKey={`exceptions.${index}.ranges`}
                          errors={errors}
                          context=""
                          emptyLabel="Absent toute la journée"
                          readOnly
                        />
                      ) : (
                        <>
                          <div className="fx-exception__dates-fields">
                            <Form.Group controlId={`exception-${index}-start`}>
                              <Form.Label>Du</Form.Label>
                              <Form.Control
                                type="date"
                                size="sm"
                                isInvalid={!!dateError}
                                // Une période en cours garde sa date de début passée :
                                // sinon le navigateur refuse d'envoyer le formulaire.
                                min={exception.startDate < today ? exception.startDate : today}
                                value={exception.startDate}
                                onChange={(event) => {
                                  const startDate = event.target.value;
                                  update(index, {
                                    startDate,
                                    endDate:
                                      exception.endDate < startDate ? startDate : exception.endDate,
                                  });
                                }}
                              />
                            </Form.Group>
                            <Form.Group controlId={`exception-${index}-end`}>
                              <Form.Label>Au</Form.Label>
                              <Form.Control
                                type="date"
                                size="sm"
                                isInvalid={!!dateError}
                                min={exception.startDate}
                                value={exception.endDate}
                                onChange={(event) => update(index, { endDate: event.target.value })}
                              />
                            </Form.Group>
                          </div>
                          {dateError && (
                            <p className="fx-availability__error mb-3" role="alert">
                              {dateError}
                            </p>
                          )}

                          <div
                            className="fx-exception__kind"
                            role="radiogroup"
                            aria-label={`Type de date : ${label}`}
                          >
                            <Form.Check
                              type="radio"
                              id={`exception-${index}-off`}
                              name={`exception-${index}-kind`}
                              label="Congé (absent toute la journée)"
                              checked={exception.isOff}
                              onChange={() => update(index, { isOff: true, ranges: [] })}
                            />
                            <Form.Check
                              type="radio"
                              id={`exception-${index}-hours`}
                              name={`exception-${index}-kind`}
                              label="Horaires particuliers"
                              checked={!exception.isOff}
                              onChange={() =>
                                update(index, {
                                  isOff: false,
                                  ranges: exception.ranges.length
                                    ? exception.ranges
                                    : [{ ...DEFAULT_RANGE }],
                                })
                              }
                            />
                          </div>

                          {!exception.isOff && (
                            <RangeListEditor
                              ranges={exception.ranges}
                              onChange={(ranges) => update(index, { ranges })}
                              errorKey={`exceptions.${index}.ranges`}
                              errors={errors}
                              context={`du ${label}`}
                              emptyLabel="Aucune plage"
                            />
                          )}

                          <div className="fx-exception__footer">
                            <button
                              type="button"
                              className="fx-danger-link"
                              aria-label={`Supprimer ${label}`}
                              onClick={() => {
                                onChange(value.filter((_, i) => i !== index));
                                onOpenChange(null);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              Supprimer
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {!readOnly && addButton}
        </>
      )}
    </section>
  );
};

export default AvailabilityExceptionsEditor;
